/**
 * 서비스워커 대기 상한. 등록이 이 시간 안에 활성화되지 않으면 없는 것으로 보고 진행한다.
 *
 * 활성화가 정상적으로 일어나는 경우는 수백 ms 안에 끝나므로, 이 값은 "정상 활성화를 놓치지
 * 않을 만큼 넉넉하되, 로그아웃 클릭이 멈춘 것처럼 보이지 않을 만큼 짧게" 잡은 값이다.
 */
export const SERVICE_WORKER_READY_TIMEOUT_MS = 3_000;

/**
 * 활성 서비스워커 등록을 상한 안에서 가져온다. 못 가져오면 null 이다.
 *
 * `navigator.serviceWorker.ready` 를 직접 await 하면 안 된다 — 거부도 타임아웃도 없이 활성
 * 등록이 생길 때까지 대기만 하므로, 등록이 없는 상태에서는 영영 풀리지 않고 `.catch` 도
 * 걸리지 않는다. 그 await 뒤에 있는 코드는 통째로 실행되지 않는다.
 *
 * 등록이 없는 상태는 예외가 아니라 정상적으로 발생한다:
 * - 배포 배너의 '새로고침'이 리로드 직전 모든 등록을 unregister 한다
 *   (`DeploymentBanner.tsx` — precache 가 옛 셸을 서빙해 생기던 새로고침 루프 차단용)
 * - dev 는 등록 자체를 하지 않는다 (`index.html` — sw.js 자리에 SPA fallback 이 잡히는 것 방지)
 *
 * 그래서 호출부마다 막지 않고 이 함수 한 곳에서 상한을 준다. 실패를 예외로 올리지 않는 것도
 * 계약의 일부다 — 호출부 셋(로그아웃 정리 · 구독 상태 조회 · 구독)은 모두 서비스워커가 없으면
 * 그냥 넘어가야 하는 자리라, null 을 받아 각자의 방식으로 진행한다.
 */
export function getReadyServiceWorker(
  timeoutMs: number = SERVICE_WORKER_READY_TIMEOUT_MS
): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), timeoutMs);
    const settle = (reg: ServiceWorkerRegistration | null) => {
      clearTimeout(timer);
      resolve(reg);
    };

    navigator.serviceWorker.ready.then(settle, () => settle(null));
  });
}
